export const COMPANY = {
  name: "Ferry Freight LLC",
  short: "Ferry Freight",
  email: "ferry@ferryfreight.us",
  phone: "(909) 359-9295",
  location: "California, USA",
};

/* Social profiles — placeholder URLs until the owner shares real handles. */
export const SOCIAL = [
  { key: "linkedin", label: "LinkedIn", href: "https://www.linkedin.com/company/ferry-freight-llc" },
  { key: "facebook", label: "Facebook", href: "https://www.facebook.com/ferryfreightllc" },
  { key: "instagram", label: "Instagram", href: "https://www.instagram.com/ferryfreightllc" },
  { key: "x", label: "X (Twitter)", href: "https://x.com/ferryfreightllc" },
];

/* Memberships & affiliations — text badges until official logos are supplied. */
export const MEMBERSHIPS = [
  { abbr: "FMCSA", name: "Licensed & Bonded Broker", note: "Federal Motor Carrier Safety Administration" },
  { abbr: "TIA", name: "Member", note: "Transportation Intermediaries Association" },
  { abbr: "BBB", name: "Accredited Business", note: "Better Business Bureau" },
  { abbr: "IANA", name: "Member", note: "Intermodal Association of North America" },
  { abbr: "CTA", name: "Affiliate", note: "California Trucking Association" },
  { abbr: "SmartWay", name: "Partner", note: "EPA SmartWay Transport Partnership" },
];

export const IMG = {
  heroPort:
    "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?q=80&w=1600&auto=format&fit=crop",
  cargoShip:
    "https://images.unsplash.com/photo-1649364709011-7f0b6c7a6645?q=80&w=1600&auto=format&fit=crop",
  techAerial:
    "https://images.unsplash.com/photo-1562892302-97faedd66f1c?q=80&w=1600&auto=format&fit=crop",
  shipAerial:
    "https://images.unsplash.com/photo-1613690399151-65ea69478674?q=80&w=1800&auto=format&fit=crop",
};

export const SERVICES = [
  {
    slug: "drayage",
    name: "Port Drayage",
    tagline: "Containers in and out of the port, on time.",
    short: "Container pickups at LA, Long Beach & Oakland with appointment handling and LFD tracking.",
    image: "https://images.unsplash.com/photo-1561702469-c4239ced3f47?q=80&w=1200&auto=format&fit=crop",
    icon: "anchor",
    overview: [
      "Moving a container out of the LA or Long Beach port is a clock game: appointments, last free days, chassis shortages, congestion windows. We run that clock for you, so your container shows up at your door instead of a fee schedule.",
      "Our drayage desk works the terminals daily — we book the appointment, monitor the last free day, arrange pre-pulls when the yard can't take it yet, and get the box to your warehouse or transload dock without drama.",
    ],
    bullets: [
      "LA, Long Beach, Oakland & Seattle lane coverage",
      "Pier pass, appointments & LFD monitoring handled for you",
      "Pre-pull and drop-yard options when your dock is full",
      "Transloading containers into domestic 53' trailers",
      "Chassis included on most moves, no surprise fees",
    ],
    included: [
      "Terminal appointment scheduling",
      "Last free day (LFD) tracking & extensions",
      "Container pre-pull & drop yardage",
      "Transload from ISO to 53' domestic",
      "Preload & empty return coordination",
      "24/7 dispatch contact for every active move",
    ],
  },
  {
    slug: "intermodal",
    name: "Intermodal & Rail",
    tagline: "Long-haul economics on the steel highway.",
    short: "Rail-backed long-haul moves that cut cost per mile without cutting visibility.",
    image: "https://images.unsplash.com/photo-1559297434-fae8a1916a79?q=80&w=1200&auto=format&fit=crop",
    icon: "train",
    overview: [
      "When a lane is long and the freight is not in a rush to burn money, intermodal is the move. Rail-backed capacity on California-to-Midwest and West Coast regional lanes can trim your linehaul meaningfully on the right freight.",
      "We quote the truck and the rail option side by side, tell you honestly which one fits your transit window, and manage the dray on both ends so you never deal with two vendors for one shipment.",
    ],
    bullets: [
      "53' domestic intermodal on regional & transcontinental lanes",
      "Truck vs. rail comparison quoted on every long haul",
      "Door-to-door pricing — dray included on both ends",
      "Transit windows you can actually promise customers",
      "Cross-border and transcon routing experience",
    ],
    included: [
      "Lane analysis & mode comparison",
      "Door-to-door intermodal pricing",
      "Origin & destination dray management",
      "Shipment milestone updates",
      "Rail ramp coordination",
      "Plan B truck capacity when transit slips",
    ],
  },
  {
    slug: "full-truckload",
    name: "Full Truckload (FTL)",
    tagline: "A dedicated trailer, a dedicated plan.",
    short: "Dedicated 53' dry van capacity across California and the Southwest, day or night.",
    image: "https://images.unsplash.com/photo-1745956983820-6e960f7e8472?q=80&w=1200&auto=format&fit=crop",
    icon: "truck",
    overview: [
      "Your freight gets a full trailer and a full plan. Whether it's a single drop in Phoenix or a multi-stop run down I-5, we match the load to carriers who run the lane every week.",
      "Every FTL load ships with a named dispatcher, live tracking, and a driver who was briefed on your dock rules — not just the address.",
    ],
    bullets: [
      "Dedicated 53' dry van capacity, 48-state authority",
      "Drop trailer & live load flexibility",
      "Team drivers for tight transit windows",
      "Named dispatcher on every load",
      "Load, lock & count oversight at pickup",
    ],
    included: [
      "Lane pricing & market-rate benchmarking",
      "Carrier vetting: insurance, authority, safety record",
      "Live tracking & milestone updates",
      "Detention & lumper advocacy",
      "Weekend & off-hour pickups",
      "POD collection and delivery confirmation",
    ],
  },
  {
    slug: "ltl",
    name: "LTL Shipping",
    tagline: "Pallets without the full-trailer price tag.",
    short: "Flexible less-than-truckload service with class negotiation and dock-to-dock care.",
    image: "https://images.unsplash.com/photo-1720811559371-7b0ebd219127?q=80&w=1200&auto=format&fit=crop",
    icon: "boxes",
    overview: [
      "LTL pricing lives and dies on freight class, density and accessorials. We negotiate that game for you — correctly classified bills, disputed re-classes handled, and reweighs argued when they're wrong.",
      "For recurring pallet moves we set up standing lane pricing so you're not re-quoting the same shipment every week.",
    ],
    bullets: [
      "Correct freight class & density review on every BOL",
      "Re-weigh and re-class disputes handled for you",
      "Standing lane pricing for recurring pallet freight",
      "Liftgate, inside delivery & limited access managed",
      "Volume LTL when you outgrow one pallet",
    ],
    included: [
      "Multi-carrier LTL rate shopping",
      "BOL prep & classification review",
      "Accessorials quoted up front, not at invoice",
      "Pickup scheduling & transit tracking",
      "Claims support when handling goes wrong",
      "Consolidation options to cut cost per pallet",
    ],
  },
  {
    slug: "refrigerated",
    name: "Refrigerated",
    tagline: "Cold chain that doesn't blink.",
    short: "Temperature-controlled reefer capacity for Central Valley produce and beyond.",
    image: "https://images.unsplash.com/photo-1586206670130-4c6d8e646c9a?q=80&w=1200&auto=format&fit=crop",
    icon: "thermometer",
    overview: [
      "From Salinas berries to Fresno dairy, a degree of drift is the difference between a delivered load and a claim. We place your freight with reefer carriers who log temperatures and answer the phone.",
      "Tell us your setpoint once — we brief the driver, verify pre-cool, and monitor the box from pickup to delivery.",
    ],
    bullets: [
      "Continuous & cycle reefer from -20°F to +70°F",
      "Pre-cool verification before loading",
      "Temp log collection with every delivery",
      "Central Valley, Salinas & Yuma seasonal programs",
      "Pulp temp checks on produce loads",
    ],
    included: [
      "Setpoint briefing on every rate con",
      "Pre-cool & reefer unit check at pickup",
      "Temperature log collection at delivery",
      "Seasonal lane programs for produce shippers",
      "Washout verification between loads",
      "24/7 reefer dispatch line",
    ],
  },
  {
    slug: "flatbed",
    name: "Flatbed & Heavy Haul",
    tagline: "Steel, lumber, machinery — tarped and permitted.",
    short: "Open-deck capacity for machinery, building materials and oversized freight.",
    image: "https://images.unsplash.com/photo-1697847726197-bc8bd6cc6e4d?q=80&w=1200&auto=format&fit=crop",
    icon: "container",
    overview: [
      "Flatbed freight is a craft: proper dunnage, correct straps and chains, tarps that survive I-80 wind. We work with open-deck carriers who take pride in a clean load and carry the coverage to prove it.",
      "Oversize or overweight? We handle permits, escorts and route surveys so your machine arrives legal and on schedule.",
    ],
    bullets: [
      "Step decks, double drops & RGNs",
      "Tarp-certified drivers and clean-load standards",
      "Oversize/overweight permits & escorts arranged",
      "Route surveys for high & wide moves",
      "Machinery, steel, lumber & building materials",
    ],
    included: [
      "Permit & escort coordination",
      "Load securement to DOT standards",
      "Tarping included or quoted up front",
      "Multi-stop construction site deliveries",
      "Heavy haul routing & survey",
      "Job-site scheduling with your site foreman",
    ],
  },
  {
    slug: "warehousing",
    name: "Warehousing & Distribution",
    tagline: "One dock for storage, transload & distribution.",
    short: "Cross-dock, transload and short-term storage near the ports and along I-5.",
    image: "https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?q=80&w=1200&auto=format&fit=crop",
    icon: "warehouse",
    overview: [
      "Containers come in faster than your warehouse can swallow them. Our cross-dock partners near the ports take the box, de-van it, pallet it, and stage it until your doors are ready.",
      "We coordinate storage, transloading and outbound distribution as one move — one price, one team, no hand-off gaps.",
    ],
    bullets: [
      "Cross-dock & transload near LA / Long Beach ports",
      "Short and mid-term pallet storage",
      "De-vanning, re-palletizing & slip sheeting",
      "Outbound distribution on our truck network",
      "Support for shippers of every size",
    ],
    included: [
      "Container de-van & re-palletizing",
      "Inventory visibility while in storage",
      "Cross-dock turnaround measured in hours",
      "Outbound FTL & LTL from the warehouse",
      "Rolling & static storage options",
      "One invoice: storage + drayage + linehaul",
    ],
  },
  {
    slug: "expedited",
    name: "Expedited",
    tagline: "When 'as soon as possible' is the brief.",
    short: "Team drivers, hotshots and same-day moves for freight that can't wait.",
    image: "https://images.unsplash.com/photo-1720811559395-3ed8d1b16649?q=80&w=1200&auto=format&fit=crop",
    icon: "zap",
    overview: [
      "Line-down, short-notice, or a customer that moved the deadline — expedited freight is about the first hour, not the price sheet. We answer fast and put wheels under it fast.",
      "Solo, team, straight truck or sprinter: we match the mode to the clock and keep you updated every leg of the run.",
    ],
    bullets: [
      "Same-day and next-flight-out modes",
      "Team drivers for non-stop transit",
      "Hotshot & sprinter capacity statewide",
      "Line-down & emergency recovery moves",
      "Hourly status updates, pickup to POD",
    ],
    included: [
      "One-hour carrier commitment during dispatch hours",
      "Team & solo expedited capacity",
      "Sprinter van & straight truck options",
      "Live ETA updates throughout the run",
      "Direct driver contact when seconds matter",
      "After-hours & weekend dispatch",
    ],
  },
];

export const PARTNERS = {
  shippers: {
    title: "One broker for every lane.",
    body: "We give every delivery the respect it deserves — for you, each shipment marks a turning point, not a tracking number.",
    bullets: [
      "Reliable, fast transportation across California & the Southwest",
      "Coverage across FTL, LTL, reefer, flatbed, drayage & expedited",
      "Named dispatcher plus a 24/7 line for every active load",
      "Market-rate quotes in minutes, not days",
    ],
    cta: { label: "Request a Quote", to: "/quote" },
    image: "https://images.unsplash.com/photo-1586206670130-4c6d8e646c9a?q=80&w=1200&auto=format&fit=crop",
  },
  carriers: {
    title: "Quality freight at your fingertips.",
    body: "Carriers are the backbone of this industry. We build relationships on trust, transparency and fast, fair pay.",
    bullets: [
      "Consistent loads across California & the Southwest",
      "Quick-pay options on approved loads",
      "Fair fuel, detention and lumper terms",
      "A dispatcher who answers, day or night",
    ],
    cta: { label: "Join the Network", to: "/carriers" },
    image: "https://images.unsplash.com/photo-1745956983820-6e960f7e8472?q=80&w=1200&auto=format&fit=crop",
  },
  agents: {
    title: "Run your book. We run the back office.",
    body: "Our agents are an extension of our own team — we work together toward mutual success, not against a quota.",
    bullets: [
      "Commission splits tailored to your book",
      "Licensing, bonding & compliance handled",
      "Modern TMS, accounting & marketing support",
      "Best tools and training in the industry",
    ],
    cta: { label: "Apply as an Agent", to: "/agents" },
    image: "https://images.unsplash.com/photo-1561702469-c4239ced3f47?q=80&w=1200&auto=format&fit=crop",
  },
};

export const TESTIMONIALS = [
  {
    quote:
      "Ferry Freight took drayage off my plate. Appointments, chassis, last free days — I just get the container where it needs to be, on time.",
    name: "Dana R.",
    role: "Food & Beverage Shipper, Fresno",
  },
  {
    quote:
      "We ship produce out of the Central Valley and temperature control is everything. Loads arrive clean and the communication is constant.",
    name: "Mike T.",
    role: "Produce Shipper, Salinas",
  },
  {
    quote:
      "Quick pay actually means quick. The best brokerage I've worked with as an owner-operator — they respect the truck.",
    name: "Luis G.",
    role: "Owner-Operator, Bakersfield",
  },
];

export const COMPLIANCE = [
  "Licensed & bonded freight brokerage (FMCSA)",
  "Zero double-brokerage policy",
  "Cargo-insured loads",
  "Carrier vetting: authority, insurance & safety records",
];

export const MARQUEE_ITEMS = [
  "Port Drayage",
  "Full Truckload",
  "LTL",
  "Refrigerated",
  "Flatbed",
  "Warehousing",
  "Expedited",
  "24/7 Dispatch",
];

export const INSIGHTS = [
  {
    slug: "california-port-drayage-guide",
    title: "California Port Drayage: LFDs, Appointments & Clean Fees",
    category: "Drayage",
    date: "March 4, 2025",
    readTime: "4 min read",
    excerpt:
      "The port charges you in days, not dollars. Here's how last free days, terminal appointments and chassis splits quietly add up — and how a good drayage desk keeps them off your invoice.",
    image: "https://images.unsplash.com/photo-1578163677454-b3933804a354?q=80&w=1400&auto=format&fit=crop",
    body: `If you import through Los Angeles or Long Beach, the freight itself is rarely what costs you — the calendar is. Every container lands with a last free day (LFD), and everything after that date is a per-diem fee that climbs daily.

## The LFD clock starts at discharge
Most terminals give you four to five working days before storage and per-diem begin. The trouble is your container doesn't announce its LFD by email. A brokerage running your drayage should be tracking it the day the vessel discharges, booking the appointment while you still have runway.

## Appointments are a commodity now
Terminal appointment slots move in morning and afternoon blocks, and they go fast during peak season. Draymen who work the terminals daily know which terminals release slots at night and which ones run dual-transaction windows. That knowledge is worth real money when your warehouse has a fixed receiving window.

## Where the surprise fees hide
Chassis splits, pre-pulls, drop yards, congestion surcharges — none of these are sinister on their own, but a quote that doesn't name them up front is a quote you'll pay twice. Ask for an all-in drayage rate that lists what happens if your container needs a pre-pull or your dock can't receive for two days.

## The fix is boring: visibility
Every one of these costs is manageable with one habit — knowing the container's status before the terminal charges you for not knowing. That's the core of a good drayage program: appointment booked, LFD tracked, fee picture fixed before the box moves.`,
  },
  {
    slug: "reefer-temperature-guide",
    title: "The Reefer Temperature Guide Every Produce Shipper Should Bookmark",
    category: "Cold Chain",
    date: "February 18, 2025",
    readTime: "3 min read",
    excerpt:
      "Berries at 33°F, bananas at 58°F, onions at 45°F with ventilation. A one-page setpoint reference for the loads we move out of the Central Valley and Salinas every week.",
    image: "https://images.unsplash.com/photo-1586206670130-4c6d8e646c9a?q=80&w=1400&auto=format&fit=crop",
    body: `Temperature is the cheapest insurance in cold chain — and the most expensive thing to get wrong. A reefer set two degrees off can turn a perfect load into a claim before the truck crosses the county line.

## Know your two modes
Continuous running holds the box at setpoint the whole trip. Cycle-sentry runs the unit intermittently to save fuel and protect products that respire — onions, potatoes, some leafy goods. The wrong mode on the wrong commodity ruins load quality just as surely as a broken unit.

## Common setpoints we run
- Berries & cherries: 32–34°F, continuous, high humidity
- Leafy greens & broccoli: 32°F, continuous
- Citrus: 38–41°F depending on variety and market
- Bananas: 56–58°F, continuous — never below 55°F
- Onions & potatoes: 45–55°F, cycle with fresh-air exchange
- Dairy & cheese: 34–38°F, continuous

## Pre-cool the box, not just the freight
The most common rookie miss in produce: loading a warm trailer. Your reefer unit then spends the first hour cooling air instead of fruit. We verify pre-cool on every produce load we book — it takes the driver ninety seconds and saves everyone a dispute.

## Paperwork is part of the cold chain
Ask for the temperature log at delivery. If a receiver rejects on pulp temp, the log is the difference between a settled claim and an argument. Every reefer load we move ships with setpoint on the rate con and logs collected at the end.`,
  },
  {
    slug: "double-brokering-protection",
    title: "Double Brokering Is Back. Here's How to Protect Your Freight.",
    category: "Compliance",
    date: "January 27, 2025",
    readTime: "4 min read",
    excerpt:
      "A broker re-brokers your load to another broker without telling you, and now nobody knows who's driving your freight. The playbook to keep your loads — and your money — out of that mess.",
    image: "https://images.unsplash.com/photo-1720811559395-3ed8d1b16649?q=80&w=1400&auto=format&fit=crop",
    body: `Double brokering is the practice of accepting a shipment as a broker and then re-brokering it to another broker or carrier without disclosure. The shipper thinks one company holds the load. In reality, a chain of intermediaries is skimming margin, and nobody in the chain may be vetting the truck.

## Why it's dangerous
When a load is double-brokered, the carrier may be paid by an entity with no contract with you. Cargo can sit at a dock with no one accountable. Insurance can be falsified or forwarded from a different operation entirely. In the worst cases, freight gets held for a ransom "rate change."

## The red flags
- A carrier's MC number doesn't match the name on the truck
- Insurance certificates forwarded, not issued directly by the agent
- Drivers who don't know who the broker of record is
- Rate cons with different company names on each page
- A new brokerage emailing you from a generic domain

## What we do about it
Our policy is zero double-brokerage, in writing, on every load. We vet the carrier's authority, insurance and safety record ourselves before a truck is ever assigned, and we verify the driver matches the carrier at pickup. If a truck shows up that wasn't who we tendered, the load stops until it's resolved.

## Three questions for any broker
Who holds my freight right now? Can you confirm the trucking company's MC number and insurance directly? And will you contract, in writing, never to re-broker my load? A straight answer to all three should take less than a minute. If it takes longer, keep shopping.`,
  },
  {
    slug: "choosing-a-california-freight-broker",
    title: "Choosing a California Freight Broker: 7 Questions That Matter",
    category: "Buyer's Guide",
    date: "January 9, 2025",
    readTime: "5 min read",
    excerpt:
      "Anyone can send you a rate sheet. Seven questions that separate a logistics partner from a margin machine — from carrier vetting to how they behave when a load goes sideways.",
    image: "https://images.unsplash.com/photo-1562892302-97faedd66f1c?q=80&w=1400&auto=format&fit=crop",
    body: `Freight brokerage is a low-barrier, high-trust business. The license is public record; the quality is not. Before you hand a lane to a broker, ask these seven and listen for specifics.

## 1. How do you vet carriers?
The right answer includes FMCSA authority checks, insurance verification direct from the agent, and safety-rating reviews — not "we use load boards."

## 2. Who owns my shipment when it breaks?
You want a named dispatcher with a phone number, not a ticketing portal. Problems are solved by people who answer at 9pm.

## 3. How do you price my lane?
Honest brokers benchmark against the market and tell you why the number moved. If a quote is mysteriously 40% under everyone else, that's not a deal — it's a bait.

## 4. Do you re-broker loads?
The answer must be no, in writing.

## 5. What's your claims process?
Accidents happen even in good networks. You want a broker who collects documentation at delivery and walks the claim with you — not one who forwards an email address.

## 6. Can you handle my whole mix?
A partner who covers dry van, reefer, flatbed, drayage and warehousing keeps your freight out of a spreadsheet of one-off vendors.

## 7. What do you need from me?
Good brokers ask for dock hours, contacts and commodity details before they need them. The load profile is the job.`,
  },
];

export const NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Track", to: "/track" },
  { label: "Carriers", to: "/carriers" },
  { label: "Agents", to: "/agents" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];
